import {
  AUTO_TRADE,
  CURRENCY,
  FACTIONS,
  LEVERS,
  LEVER_EFFECTS,
  LEVER_IDS,
  MARKET,
  RESERVE,
  TRADE,
  TRADE_GOOD_IDS,
  type LeverId,
  type TradeGoodId,
} from '@sd/content';
import { t } from '@sd/i18n';
import {
  caravanCargo,
  atWar,
  contractSlots,
  emit,
  hasEmbargo,
  involves,
  isSanctioned,
  autoTradeReserve,
  outpostYield,
  techMods,
  tradeTerms,
  type Command,
  type GameState,
} from '@sd/sim';
import type { Viewer } from '../map/describe';
import { clock, num, playerName } from './format';
import { reasonText } from './planetModel';

/**
 * The «Market» tab (#57): prices with their recent history, what a deal
 * would cost the player right now (the simulation's own terms), the
 * player's currency and money printing, contracts with caravans, and the
 * economic levers. Pure: state in, numbers, words and commands out.
 */
export const ORDER_SIZES = [10, 50, 100] as const;
export const EMISSIONS = [200, 500, 1000] as const;

export interface Deal {
  readonly quantity: number;
  /** In the player's currency; per unit for the table. */
  readonly total: number;
  readonly perUnit: number;
  readonly command: Command | null;
  readonly reason: string | null;
}

export interface GoodRow {
  readonly good: TradeGoodId;
  readonly name: string;
  readonly price: number;
  /** Change over the history shown, as a share (0.05 = +5%). */
  readonly change: number;
  readonly history: readonly number[];
  readonly stock: number;
  /** The player's own cheap good, or one an embargo stops them buying. */
  readonly cheap: boolean;
  readonly embargoed: boolean;
  /** A glut or shortage the exchange has announced for the next cycle. */
  readonly warning: string | null;
}

export interface ContractRow {
  readonly id: number;
  readonly partner: string;
  /** Sell or buy, from the player's side. */
  readonly role: 'sell' | 'buy';
  readonly text: string;
  readonly status: string;
  readonly alert: boolean;
  readonly actions: readonly {
    readonly label: string;
    readonly command: Command;
    readonly primary?: boolean;
  }[];
}

export interface LeverRow {
  readonly id: LeverId;
  readonly name: string;
  /** What it does, with its numbers (#101). */
  readonly desc: string;
  readonly note: string;
  readonly active: boolean;
  /** Why it is closed for good until a technology: shown apart from a passing lack of influence. */
  readonly locked: string | null;
  /** What it would do right now, per rival, in numbers (#125). */
  readonly impact: readonly string[];
  /** Targets the player may aim it at (embargo, sanctions); empty when it takes none. */
  readonly targets: readonly {
    readonly id: string;
    readonly name: string;
    readonly command: Command;
  }[];
  /** Ready when it needs no target. */
  readonly command: Command | null;
  readonly reason: string | null;
}

export interface MarketView {
  readonly notices: readonly { readonly text: string; readonly note: string }[];
  readonly goods: readonly GoodRow[];
  readonly currency: {
    readonly rate: number;
    readonly issuer: string;
    readonly isIssuer: boolean;
    readonly fee: string;
    readonly emissions: readonly {
      readonly amount: number;
      readonly rateAfter: number;
      readonly command: Command;
    }[];
  };
  readonly rates: readonly { readonly id: string; readonly name: string; readonly rate: number }[];
  readonly contracts: readonly ContractRow[];
  readonly slots: { readonly used: number; readonly total: number };
  readonly partners: readonly { readonly id: string; readonly name: string }[];
  readonly levers: readonly LeverRow[];
  readonly influence: number;
  /** «Торговые алгоритмы» (#123): a rule per good, or why there are none yet. */
  readonly autoTrade: AutoTradeView;
}

export interface AutoTradeRow {
  readonly good: TradeGoodId;
  readonly name: string;
  readonly mode: 'off' | 'sell' | 'buy';
  /** What the next cycle takes from the stock: the line a rule works from (#143). */
  readonly reserve: number;
  /** What the rule does now, in words. */
  readonly note: string;
  readonly set: Readonly<Record<'off' | 'sell' | 'buy', Command>>;
}

export interface AutoTradeView {
  readonly locked: string | null;
  readonly rows: readonly AutoTradeRow[];
  /** One click (#123): «sell above N» for every good the planet makes now. */
  readonly sellAll: readonly Command[];
}

/** The rules of «Торговые алгоритмы» (#123) as rows: mode, the cycle's use, what happens. */
export function autoTradeView(state: GameState, v: Viewer): AutoTradeView {
  const { me, locale } = v;
  const player = state.players.find((p) => p.id === me);
  if (!player) return { locked: null, rows: [], sellAll: [] };
  if (!techMods(player.techs).autoTrade) {
    return {
      locked: t(locale, 'market.lever.locked', { tech: t(locale, 'tech.tradeAlgorithms.name') }),
      rows: [],
      sellAll: [],
    };
  }
  const report = player.planet.lastCycle;
  const rows = TRADE_GOOD_IDS.map((good): AutoTradeRow => {
    const rule = player.autoTrade?.[good];
    const mode = rule?.mode ?? 'off';
    // From the planet's needs (#143): a number set by hand is only kept for old rules.
    const keep = rule?.keep ?? AUTO_TRADE.defaultKeep;
    const reserve = Math.ceil(autoTradeReserve(player, good) + keep);
    const made = report ? report.produced[good] - report.consumed[good] : 0;
    const command = (r: { mode: 'sell' | 'buy'; keep: number } | null): Command => ({
      type: 'setAutoTrade',
      playerId: me,
      good,
      rule: r,
    });
    const note =
      mode === 'off'
        ? t(locale, 'market.auto.off')
        : mode === 'sell'
          ? made > 0
            ? t(locale, 'market.auto.selling', { keep: reserve })
            : t(locale, 'market.auto.notMade', { keep: reserve })
          : t(locale, 'market.auto.buying', { keep: reserve });
    return {
      good,
      name: t(locale, `resource.${good}`),
      mode,
      reserve,
      note,
      set: {
        off: command(null),
        sell: command({ mode: 'sell', keep: AUTO_TRADE.defaultKeep }),
        buy: command({ mode: 'buy', keep: AUTO_TRADE.defaultKeep }),
      },
    };
  });
  // Made now: the planet's net output, extraction included.
  const fromOutposts = (good: TradeGoodId) =>
    state.outposts
      .filter((o) => o.owner === me && o.good === good && o.readyTick <= state.tick)
      .reduce((sum, o) => sum + outpostYield(player, o), 0);
  const sellAll = rows
    .filter((r) => {
      const made = report ? report.produced[r.good] - report.consumed[r.good] : 0;
      return made + fromOutposts(r.good) > 0 && r.mode !== 'sell';
    })
    .map((r) => r.set.sell);
  return { locked: null, rows, sellAll };
}

/** The terms of buying or selling `quantity` of `good`, as the simulation would settle them. */
export function deal(
  state: GameState,
  v: Viewer,
  good: TradeGoodId,
  side: 'buy' | 'sell',
  quantity: number,
): Deal {
  const terms = tradeTerms(state, v.me, good, side, quantity);
  if (!terms.ok) {
    return {
      quantity,
      total: 0,
      perUnit: 0,
      command: null,
      reason: reasonText(v.locale, terms.reason),
    };
  }
  return {
    quantity,
    total: terms.local,
    perUnit: terms.local / quantity,
    command: { type: side === 'buy' ? 'marketBuy' : 'marketSell', playerId: v.me, good, quantity },
    reason: state.pause ? reasonText(v.locale, 'paused') : null,
  };
}

function contractRow(state: GameState, v: Viewer, c: GameState['contracts'][number]): ContractRow {
  const { locale } = v;
  const role = c.seller === v.me ? 'sell' : 'buy';
  const partnerId = role === 'sell' ? c.buyer : c.seller;
  const partner = playerName(locale, v.setup, partnerId);
  const good = t(locale, `resource.${c.good}`).toLowerCase();
  const terms =
    c.price !== null
      ? t(locale, 'market.contract.price', { price: num(locale, c.price, 1) })
      : t(locale, 'market.contract.barter', {
          quantity: c.barter!.quantity,
          good: t(locale, `resource.${c.barter!.good}`).toLowerCase(),
        });
  const text = t(locale, role === 'sell' ? 'market.contract.sell' : 'market.contract.buy', {
    quantity: c.quantity,
    good,
    terms,
    left: c.deliveriesLeft,
  });
  const actions: ContractRow['actions'][number][] = [];
  let status: string;
  let alert = false;
  if (c.status === 'proposed') {
    if (c.proposedBy === v.me) status = t(locale, 'market.contract.waiting');
    else {
      status = t(locale, 'market.contract.offered');
      alert = true;
      actions.push({
        label: t(locale, 'market.contract.accept'),
        command: { type: 'acceptContract', playerId: v.me, contractId: c.id },
        primary: true,
      });
    }
  } else if (!c.caravan) {
    status =
      role === 'sell'
        ? t(locale, 'market.contract.noCaravan')
        : t(locale, 'market.contract.awaitCaravan');
    alert = role === 'sell';
    if (role === 'sell') {
      // The idle fleet with enough hold closest to home; contracts never take a warship off guard duty.
      const busy = new Set(state.contracts.flatMap((x) => (x.caravan ? [x.caravan.fleetId] : [])));
      const needs = Math.max(c.quantity, c.barter?.quantity ?? 0);
      const fleet = state.fleets.find(
        (f) => f.owner === v.me && !busy.has(f.id) && caravanCargo(state, c, f) >= needs,
      );
      if (fleet) {
        actions.push({
          label: t(locale, 'market.contract.assign', { id: fleet.id }),
          command: { type: 'assignCaravan', playerId: v.me, contractId: c.id, fleetId: fleet.id },
          primary: true,
        });
      } else status = t(locale, 'market.contract.needTransport', { n: needs });
    }
  } else {
    const phase = c.caravan.phase;
    status = t(locale, `market.caravan.${phase}`);
    if (c.missed > 0) {
      status += ` · ${t(locale, 'market.contract.missed', { n: c.missed, max: TRADE.missedToBreak })}`;
      alert = true;
    }
  }
  if (isSanctioned(state.levers, v.me) || isSanctioned(state.levers, partnerId)) {
    status = t(locale, 'market.contract.frozen');
    alert = true;
  }
  actions.push({
    label: t(locale, 'market.contract.cancel'),
    command: { type: 'cancelContract', playerId: v.me, contractId: c.id },
  });
  return { id: c.id, partner, role, text, status, alert, actions };
}

/**
 * Each player's share of the system's output in the last cycle — planet and
 * extraction at today's prices, or with `prices` changed (#125: what a lever
 * would do, in numbers).
 */
export function outputShares(
  state: GameState,
  prices: Partial<Record<TradeGoodId, number>> = {},
): Map<string, number> {
  const value = new Map<string, number>();
  for (const p of state.players) {
    const made = { ...(p.planet.lastCycle?.produced ?? {}) } as Record<TradeGoodId, number>;
    for (const o of state.outposts) {
      if (o.owner === p.id && o.readyTick <= state.tick)
        made[o.good] = (made[o.good] ?? 0) + outpostYield(p, o);
    }
    value.set(
      p.id,
      TRADE_GOOD_IDS.reduce(
        (sum, g) => sum + (made[g] ?? 0) * (prices[g] ?? state.market[g].price),
        0,
      ),
    );
  }
  const total = [...value.values()].reduce((a, b) => a + b, 0) || 1;
  return new Map([...value].map(([id, v]) => [id, v / total]));
}

/** Dumping a good (#125): each player's output share now and while its price is down. */
export function dumpingImpact(state: GameState, v: Viewer, good: TradeGoodId): string[] {
  const now = outputShares(state);
  const after = outputShares(state, {
    [good]: state.market[good].price * LEVER_EFFECTS.dumpingPriceFactor,
  });
  return state.players.map((p) =>
    t(v.locale, 'market.lever.impact.share', {
      name: playerName(v.locale, v.setup, p.id),
      now: Math.round((now.get(p.id) ?? 0) * 100),
      then: Math.round((after.get(p.id) ?? 0) * 100),
    }),
  );
}

/**
 * `history` is the price at each closed cycle, oldest first, kept by the
 * panel: the state holds only the current price.
 */
export function marketView(
  state: GameState,
  v: Viewer,
  history: Readonly<Partial<Record<TradeGoodId, readonly number[]>>>,
): MarketView | null {
  const { locale, me } = v;
  const player = state.players.find((p) => p.id === me);
  if (!player) return null;
  const name = (id: string) => playerName(locale, v.setup, id);
  const rivals = state.players.filter((p) => p.id !== me);

  const notices: MarketView['notices'][number][] = [];
  const sanctions = state.levers.find(
    (l) => l.kind === 'sanctions' && l.target === me && l.status === 'active',
  );
  if (sanctions) {
    notices.push({
      text: t(locale, 'market.notice.sanctions', { time: clock(sanctions.until - state.tick) }),
      note: t(locale, 'market.notice.sanctionsNote', {
        names: sanctions.by.map(name).join(', '),
        pct: Math.round(MARKET.fee * 100),
      }),
    });
  }

  const goods: GoodRow[] = TRADE_GOOD_IDS.map((good) => {
    const price = state.market[good].price;
    const past = [...(history[good] ?? []), price];
    const first = past[0] ?? price;
    const embargoed = state.players.some(
      (p) => FACTIONS[p.faction].cheapGood === good && hasEmbargo(state.levers, p.id, me),
    );
    const warning = state.marketWarnings.find((w) => w.good === good);
    return {
      good,
      name: t(locale, `resource.${good}`),
      price,
      change: first > 0 ? price / first - 1 : 0,
      history: past,
      stock: player.goods[good],
      cheap: FACTIONS[player.faction].cheapGood === good,
      embargoed,
      warning: warning ? t(locale, `market.${warning.kind}.name`) : null,
    };
  });

  const issuer = state.reserve.issuer;
  const weight = issuer === me ? RESERVE.issuerInflationWeight : 1;
  const fee = issuer === me ? 0 : techMods(player.techs).conversionFee;

  const contracts = state.contracts
    .filter((c) => involves(c, me))
    .map((c) => contractRow(state, v, c));

  const levers: LeverRow[] = LEVER_IDS.map((id) => {
    const def = LEVERS[id];
    const mine = state.levers.find((l) => l.kind === id && l.by.includes(me));
    const known = player.techs.includes(def.requiresTech);
    const locked = known
      ? null
      : t(locale, 'market.lever.locked', { tech: t(locale, `tech.${def.requiresTech}.name`) });
    const short = !known
      ? reasonText(locale, 'tech required', def.requiresTech)
      : player.influence < def.cost
        ? reasonText(locale, 'not enough influence')
        : null;
    const note = mine
      ? t(locale, mine.status === 'gathering' ? 'market.lever.gathering' : 'market.lever.active', {
          time: clock(mine.until - state.tick),
          target: mine.target ? name(mine.target) : '',
        })
      : t(locale, 'market.lever.cost', { cost: def.cost, cycles: def.cycles });
    const ready = !mine && !short;
    const targeted = id === 'embargo' || id === 'sanctions';
    // What it would do to each rival now, in numbers (#125: the levers felt indirect).
    const impact = rivals
      .map((r) => {
        const who = name(r.id);
        if (id === 'embargo') {
          const tied = state.contracts.filter((c) => involves(c, me) && involves(c, r.id)).length;
          return t(locale, 'market.lever.impact.embargo', {
            name: who,
            good: t(locale, `resource.${FACTIONS[player.faction].cheapGood}`).toLowerCase(),
            contracts: tied,
          });
        }
        if (id === 'sanctions') {
          return t(
            locale,
            state.reserve.issuer === r.id
              ? 'market.lever.impact.sanctionsIssuer'
              : 'market.lever.impact.sanctions',
            { name: who, pct: Math.round(LEVER_EFFECTS.sanctionsSurcharge * 100) },
          );
        }
        if (id === 'tariff') {
          const caravans = state.contracts.filter(
            (c) => c.caravan && (c.seller === r.id || c.buyer === r.id) && !involves(c, me),
          ).length;
          return t(locale, 'market.lever.impact.tariff', { name: who, n: caravans });
        }
        return '';
      })
      .filter(Boolean);
    return {
      id,
      name: t(locale, `lever.${id}.name`),
      desc: t(locale, `lever.${id}.desc`, {
        tariff: Math.round(LEVER_EFFECTS.tariffShare * 100),
        price: Math.round(LEVER_EFFECTS.dumpingPriceFactor * 100),
        surcharge: Math.round(LEVER_EFFECTS.sanctionsSurcharge * 100),
        backers: LEVER_EFFECTS.sanctionsBackers,
        cycles: def.cycles,
      }),
      note: short && !mine && !locked ? `${note} · ${short}` : note,
      active: !!mine,
      locked: mine ? null : locked,
      impact,
      targets:
        ready && targeted
          ? rivals.map((p) => ({
              id: p.id,
              name: name(p.id),
              command: { type: 'useLever', playerId: me, lever: id, target: p.id } as Command,
            }))
          : [],
      // Dumping sells a good: the panel adds the good and quantity the player picks.
      command: ready && id === 'tariff' ? { type: 'useLever', playerId: me, lever: id } : null,
      reason: mine ? null : short,
    };
  });

  return {
    notices,
    goods,
    currency: {
      rate: player.currency.rate,
      issuer: issuer ? name(issuer) : t(locale, 'market.currency.noIssuer'),
      isIssuer: issuer === me,
      fee: `${num(locale, fee * 100)}%`,
      emissions: EMISSIONS.filter((a) => a <= CURRENCY.maxEmission).map((amount) => ({
        amount,
        rateAfter: emit(player.currency, player.planet.population, amount, weight).rate,
        command: { type: 'emit', playerId: me, amount } as Command,
      })),
    },
    rates: state.players.map((p) => ({ id: p.id, name: name(p.id), rate: p.currency.rate })),
    contracts,
    slots: {
      used: state.contracts.filter((c) => involves(c, me)).length,
      total: contractSlots(player.planet, techMods(player.techs)),
    },
    partners: rivals
      .filter((p) => !atWar(state, me, p.id))
      .map((p) => ({ id: p.id, name: name(p.id) })),
    levers,
    influence: player.influence,
    autoTrade: autoTradeView(state, v),
  };
}

/** A contract offer from the player's form, with the simulation's limits applied. */
export function proposal(
  v: Viewer,
  form: {
    readonly other: string;
    readonly role: 'sell' | 'buy';
    readonly good: TradeGoodId;
    readonly quantity: number;
    readonly price: number;
    readonly deliveries: number;
  },
): Command {
  return {
    type: 'proposeContract',
    playerId: v.me,
    other: form.other,
    role: form.role,
    good: form.good,
    quantity: Math.max(1, Math.min(MARKET.maxOrderUnits, Math.round(form.quantity))),
    price: Math.max(0.1, Math.round(form.price * 10) / 10),
    deliveries: Math.max(1, Math.min(TRADE.maxDeliveries, Math.round(form.deliveries))),
  };
}
